import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

const KEY = 'deals';

export function useDeals(params = {}) {
  return useQuery({
    queryKey: [KEY, params],
    queryFn: () => api.get('/deals', { params }).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
  });
}

export function useDeal(id) {
  return useQuery({
    queryKey: [KEY, id],
    queryFn: () => api.get(`/deals/${id}`).then((r) => r.data),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePipelineSummary(params = {}) {
  return useQuery({
    queryKey: [KEY, 'pipeline', params],
    queryFn: () => api.get('/deals/pipeline', { params }).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
  });
}

export function useForecast(params = {}) {
  return useQuery({
    queryKey: [KEY, 'forecast', params],
    queryFn: () => api.get('/deals/forecast', { params }).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/deals', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useUpdateDeal(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch(`/deals/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, id] });
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useMoveDealStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stage }) => api.patch(`/deals/${id}/stage`, { stage }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useCloseDeal(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/deals/${id}/close`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, id] });
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useCloneDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/deals/${id}/clone`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useDeleteDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/deals/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

// Commissions
export function useCommissions(dealId) {
  return useQuery({
    queryKey: [KEY, dealId, 'commissions'],
    queryFn: () => api.get(`/deals/${dealId}/commissions`).then((r) => r.data),
    enabled: !!dealId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAddCommission(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/deals/${dealId}/commissions`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}

export function useUpdateCommission(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cid, ...body }) => api.patch(`/deals/${dealId}/commissions/${cid}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}

export function useDeleteCommission(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cid) => api.delete(`/deals/${dealId}/commissions/${cid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}

// Components
export function useAddComponent(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/deals/${dealId}/components`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}

export function useUpdateComponent(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cid, ...body }) => api.patch(`/deals/${dealId}/components/${cid}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}

export function useDeleteComponent(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cid) => api.delete(`/deals/${dealId}/components/${cid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}

// Commission approval workflow
export function useApproveCommission(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/deals/${dealId}/approve-commission`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}

export function useApproveForComms(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/deals/${dealId}/approve-for-comms`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}

export function useMarkCommissionPaid(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paid_date) => api.post(`/deals/${dealId}/commission-paid`, { paid_date }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}
